import { relations, sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  check,
  foreignKey,
  jsonb,
  pgTable,
  primaryKey,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";

import { group } from "../../auth/group/group.sql";
import { scope } from "../../auth/scope/scope.sql";
import { user } from "../../auth/core/user.sql";
import { temporalDate, temporalInstant } from "../../utils/sql";
import { plannedMealV2 } from "../planned-meal/planned-meal.sql";
import type {
  MealPlanningSessionMemberContextData,
  MealPlanningSessionScheduleConfigData,
} from "./types";

export const mealPlanningSession = pgTable(
  "meal_planning_session",
  {
    id: uuid("id")
      .primaryKey()
      .references(() => group.id),
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    scopeId: uuid("scope_id")
      .references((): AnyPgColumn => scope.id)
      .notNull(),
    dateStart: temporalDate("date_start").notNull(),
    dateEnd: temporalDate("date_end").notNull(),
    timeZone: text("time_zone").notNull(),
  },
  (t) => [
    unique("meal_planning_session_scope_id_unique").on(t.scopeId, t.id),
    check("meal_planning_session_date_range", sql`${t.dateStart} <= ${t.dateEnd}`),
  ],
);

export const mealPlanningSessionRelations = relations(mealPlanningSession, ({ one, many }) => ({
  group: one(group, {
    fields: [mealPlanningSession.id],
    references: [group.id],
  }),
  scope: one(scope, {
    fields: [mealPlanningSession.scopeId],
    references: [scope.id],
  }),
  plannedMealLinks: many(mealPlanningSessionPlannedMeal),
  scheduleConfig: one(mealPlanningSessionScheduleConfig, {
    fields: [mealPlanningSession.id],
    references: [mealPlanningSessionScheduleConfig.mealPlanningSessionId],
  }),
  memberContexts: many(mealPlanningSessionMemberContext),
}));

export const mealPlanningSessionPlannedMeal = pgTable(
  "meal_planning_session_planned_meal",
  {
    mealPlanningSessionId: uuid("meal_planning_session_id").notNull(),
    scopeId: uuid("scope_id").notNull(),
    plannedMealId: uuid("planned_meal_id").notNull(),
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
  },
  (t) => [
    primaryKey({
      name: "meal_planning_session_planned_meal_pk",
      columns: [t.mealPlanningSessionId, t.plannedMealId],
    }),
    foreignKey({
      name: "meal_planning_session_planned_meal_session_fk",
      columns: [t.scopeId, t.mealPlanningSessionId],
      foreignColumns: [mealPlanningSession.scopeId, mealPlanningSession.id],
    }),
    foreignKey({
      name: "meal_planning_session_planned_meal_meal_fk",
      columns: [t.scopeId, t.plannedMealId],
      foreignColumns: [plannedMealV2.scopeId, plannedMealV2.id],
    }),
  ],
);

export const mealPlanningSessionPlannedMealRelations = relations(
  mealPlanningSessionPlannedMeal,
  ({ one }) => ({
    mealPlanningSession: one(mealPlanningSession, {
      fields: [
        mealPlanningSessionPlannedMeal.scopeId,
        mealPlanningSessionPlannedMeal.mealPlanningSessionId,
      ],
      references: [mealPlanningSession.scopeId, mealPlanningSession.id],
    }),
    plannedMeal: one(plannedMealV2, {
      fields: [
        mealPlanningSessionPlannedMeal.scopeId,
        mealPlanningSessionPlannedMeal.plannedMealId,
      ],
      references: [plannedMealV2.scopeId, plannedMealV2.id],
    }),
  }),
);

export const mealPlanningSessionScheduleConfig = pgTable("meal_planning_session_schedule_config", {
  mealPlanningSessionId: uuid("meal_planning_session_id")
    .primaryKey()
    .references(() => mealPlanningSession.id),
  timeCreated: temporalInstant("time_created").notNull(),
  timeUpdated: temporalInstant("time_updated").notNull(),
  data: jsonb("data").$type<MealPlanningSessionScheduleConfigData>().notNull(),
});

export const mealPlanningSessionScheduleConfigRelations = relations(
  mealPlanningSessionScheduleConfig,
  ({ one }) => ({
    mealPlanningSession: one(mealPlanningSession, {
      fields: [mealPlanningSessionScheduleConfig.mealPlanningSessionId],
      references: [mealPlanningSession.id],
    }),
  }),
);

export const mealPlanningSessionMemberContext = pgTable(
  "meal_planning_session_member_context",
  {
    mealPlanningSessionId: uuid("meal_planning_session_id")
      .references(() => mealPlanningSession.id)
      .notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    data: jsonb("data").$type<MealPlanningSessionMemberContextData>().notNull(),
  },
  (t) => [
    primaryKey({
      name: "meal_planning_session_member_context_pk",
      columns: [t.mealPlanningSessionId, t.userId],
    }),
  ],
);

export const mealPlanningSessionMemberContextRelations = relations(
  mealPlanningSessionMemberContext,
  ({ one }) => ({
    mealPlanningSession: one(mealPlanningSession, {
      fields: [mealPlanningSessionMemberContext.mealPlanningSessionId],
      references: [mealPlanningSession.id],
    }),
  }),
);
